import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpenCheck, CheckCircle2, ChevronRight, Clock3, GraduationCap, Loader2, Lock, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { badge, btnPrimary, card, inputCls, Skeleton, Stagger, Item } from "@/components/portal/ui";

function GateScreen({ status, onRequested }) {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/portal/lms/access/request", { reason });
      toast.success("Permintaan akses terkirim ke admin/pastoral/leader");
      onRequested();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };
  if (status === "pending") {
    return (
      <div className={`${card} text-center`} data-testid="elearn-pending">
        <span className="w-16 h-16 mx-auto rounded-3xl bg-amber-100 text-amber-600 grid place-items-center"><Clock3 className="w-8 h-8" /></span>
        <h2 className="mt-4 text-xl font-extrabold text-slate-900">Menunggu Persetujuan</h2>
        <p className="mt-2 text-sm text-slate-500">Permintaan aksesmu sedang ditinjau oleh admin/pastoral/leader komunitas. Kamu akan menerima notifikasi saat disetujui.</p>
      </div>
    );
  }
  return (
    <div className={card} data-testid="elearn-locked">
      <span className="w-16 h-16 rounded-3xl bg-slate-900 text-amber-300 grid place-items-center"><Lock className="w-8 h-8" /></span>
      <h2 className="mt-4 text-xl font-extrabold text-slate-900">Menu Khusus Jemaat</h2>
      <p className="mt-2 text-sm text-slate-500">GIM E-Learning hanya dapat diakses setelah kamu mengajukan permintaan dan disetujui oleh admin/pastoral/leader RC komunitasmu.</p>
      {status === "rejected" && <p className="mt-3 text-xs font-bold text-red-600 bg-red-50 rounded-xl px-3 py-2">Permintaan sebelumnya belum disetujui. Kamu bisa mengajukan ulang.</p>}
      <form onSubmit={submit} className="mt-5 space-y-3">
        <textarea data-testid="elearn-reason-input" className={`${inputCls} min-h-24`} placeholder="Alasan mengikuti GIM E-Learning (opsional)" value={reason} onChange={(e) => setReason(e.target.value)} />
        <button data-testid="elearn-request-button" disabled={loading} className={`${btnPrimary} w-full`}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Request Akses</>}
        </button>
      </form>
    </div>
  );
}

function CourseCard({ c }) {
  const state = c.locked ? ["warn", "Terkunci"] : c.completed ? ["success", "Selesai"] : c.enrollment === "approved" ? ["info", `${c.percent}%`] : c.enrollment === "pending" ? ["warn", "Menunggu"] : ["info", "Daftar"];
  const body = (
    <div className={`${card} block !p-0 overflow-hidden ${c.locked ? "opacity-70" : ""}`}>
      {c.cover_url && <img src={fileUrl(c.cover_url)} alt="" className="w-full h-32 object-cover" />}
      <div className="p-5">
        <div className="flex items-center justify-between">
          <span className={`text-[11px] font-bold rounded-full px-3 py-1 ${badge[state[0]]}`}>{c.locked ? <Lock className="w-3 h-3 inline mr-1" /> : c.completed ? <CheckCircle2 className="w-3 h-3 inline mr-1" /> : null}{state[1]}</span>
          <span className="text-[11px] font-semibold text-slate-400">{c.category || "Umum"}</span>
        </div>
        <p className="mt-3 font-extrabold text-slate-900 leading-snug">{c.title}</p>
        <p className="text-xs text-slate-500 mt-1 line-clamp-2">{c.locked ? c.locked_reason : c.description}</p>
        <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
          <span className="flex items-center gap-1"><GraduationCap className="w-3.5 h-3.5" />{c.instructor || "Tim GIM"}</span>
          <span className="flex items-center gap-1"><BookOpenCheck className="w-3.5 h-3.5" />{c.lessons_count} materi <ChevronRight className="w-3.5 h-3.5" /></span>
        </div>
        {c.enrollment === "approved" && <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-blue-600" style={{ width: `${c.percent}%` }} /></div>}
      </div>
    </div>
  );
  if (c.locked) return <button type="button" onClick={() => toast.error(c.locked_reason)} data-testid={`course-card-${c.id}`} className="w-full text-left">{body}</button>;
  return <Link to={`/portal/elearning/${c.id}`} data-testid={`course-card-${c.id}`}>{body}</Link>;
}

export default function ELearning() {
  const { user, setUser } = useAuth();
  const [courses, setCourses] = useState(null);
  const status = user.elearning_access || "none";

  const loadCourses = () => {
    if (status === "approved") api.get("/portal/lms/courses").then((r) => setCourses(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => loadCourses(), [status]);

  const onRequested = () => setUser((u) => ({ ...u, elearning_access: "pending" }));

  return (
    <div data-testid="portal-elearning">
      <div className="relative overflow-hidden rounded-b-[2.5rem] bg-slate-900 text-white px-6 pt-12 pb-10">
        <div className="absolute -top-10 -right-10 w-44 h-44 rounded-full bg-blue-600/30" />
        <p className="relative text-xs font-bold uppercase tracking-[0.2em] text-blue-300">GIM E-Learning</p>
        <h1 className="relative text-3xl font-extrabold tracking-tight mt-1">Ruang Belajar</h1>
        <p className="relative text-sm text-white/50 mt-2">Course pemuridan dengan sertifikat resmi GIM Church.</p>
      </div>
      <div className="px-5 mt-6">
        {status !== "approved" ? <GateScreen status={status} onRequested={onRequested} /> : !courses ? <Skeleton className="h-64" /> : (
          <Stagger className="space-y-4">
            {courses.length === 0 && <p className={`${card} text-center text-sm text-slate-500`} data-testid="elearn-empty">Belum ada course yang tersedia untukmu.</p>}
            {courses.map((c) => <Item key={c.id}><CourseCard c={c} /></Item>)}
          </Stagger>
        )}
      </div>
    </div>
  );
}
